import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaUserPlus, FaEnvelope, FaTimes, FaUserCheck, FaClock, FaTrash, FaSearch, FaUser } from 'react-icons/fa';
import { getMembers, inviteMemberByEmail, removeMember, searchUsers } from '../api/client';
import ConfirmDialog from './ConfirmDialog';

const GroupMembers = ({ itineraryId, onClose, inline = false, isOwner = true }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add member states
  const [email, setEmail] = useState('');
  const [includePhotos, setIncludePhotos] = useState(true);
  const [adding, setAdding] = useState(false);

  // Typeahead search states
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, memberId: null });

  useEffect(() => {
    loadMembers();
  }, [itineraryId]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search function - runs quickly with minimal debounce
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setSearchResults([]);
      setShowDropdown(false);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await searchUsers(query);
      // Filter out existing members - API returns { results: [] }
      const memberEmails = members.map(m => m.email?.toLowerCase());
      const filteredResults = (response.results || []).filter(
        user => !memberEmails.includes(user.email?.toLowerCase())
      );
      setSearchResults(filteredResults);
      setShowDropdown(filteredResults.length > 0);
    } catch (err) {
      console.error('Error searching users:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [members]);

  // Handle email input change with minimal debounce for fast response
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    // Show loading immediately when typing
    if (value.length >= 1) {
      setSearchLoading(true);
    }

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Very short debounce (50ms) - just enough to batch rapid keystrokes
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value);
    }, 50);
  };

  // Handle selecting a user from search results
  const handleSelectUser = (user) => {
    setEmail(user.email);
    setShowDropdown(false);
    setSearchResults([]);
  };

  const loadMembers = async () => {
    try {
      setLoading(true);
      const data = await getMembers(itineraryId);
      setMembers(data.members || []);
    } catch (err) {
      setError('Failed to load members');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteByEmail = async () => {
    if (!email) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setAdding(true);
      setError('');
      const result = await inviteMemberByEmail(itineraryId, email, includePhotos);
      if (result.status === 'active') {
        setSuccess('Member added successfully!');
      } else {
        setSuccess('Invitation sent successfully!');
      }
      setEmail('');
      loadMembers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to invite member');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = (memberId) => {
    setConfirmDialog({ isOpen: true, memberId });
  };

  const confirmRemoveMember = async () => {
    const memberId = confirmDialog.memberId;
    setConfirmDialog({ isOpen: false, memberId: null });

    try {
      setError('');
      await removeMember(itineraryId, memberId);
      setSuccess('Member removed');
      loadMembers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const content = (
    <>
      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
          {success}
        </div>
      )}

      {/* Add Member Section - Email Invite with Typeahead (only for owners) */}
      {isOwner && (
        <div className="mb-6 p-4 bg-neutral-50 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <FaEnvelope className="text-secondary-500" />
            <h3 className="font-medium text-neutral-900">Invite Member by Email</h3>
          </div>

          <div className="flex gap-2 mb-3">
            <div className="flex-1 relative">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm" />
                <input
                  ref={inputRef}
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onFocus={() => email.length >= 2 && searchResults.length > 0 && setShowDropdown(true)}
                  placeholder="Search or enter email address..."
                  className="w-full pl-9 pr-3 py-2 border-2 border-neutral-300 rounded-lg focus:ring-4 focus:ring-primary-100 outline-none focus:border-primary-500"
                  autoComplete="off"
                />
                {searchLoading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>

              {/* Typeahead Dropdown */}
              {showDropdown && searchResults.length > 0 && (
                <div
                  ref={dropdownRef}
                  className="absolute z-50 w-full mt-1 bg-white border-2 border-neutral-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                >
                  {searchResults.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleSelectUser(user)}
                      className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-primary-50 transition-colors text-left border-b border-neutral-100 last:border-b-0"
                    >
                      <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <FaUser className="text-white text-xs" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-neutral-900 truncate">
                          {user.full_name || 'User'}
                        </p>
                        <p className="text-sm text-neutral-500 truncate">
                          {user.email}
                        </p>
                      </div>
                      <span className="text-xs text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full flex-shrink-0">
                        Registered
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={handleInviteByEmail}
              disabled={!email || adding}
              className="px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              {adding ? '...' : 'Invite'}
            </button>
          </div>

          <p className="text-xs text-neutral-500 mb-3">
            Start typing to search registered users, or enter any email address to send an invitation.
          </p>

          {/* Include Photos Toggle */}
          <label className="flex items-center gap-2 text-sm text-neutral-600">
            <input
              type="checkbox"
              checked={includePhotos}
              onChange={(e) => setIncludePhotos(e.target.checked)}
              className="rounded border-neutral-300 text-primary-500 focus:ring-primary-500"
            />
            Allow member to view photos
          </label>
        </div>
      )}

      {/* Members List */}
      <div>
        <h3 className="font-medium text-neutral-900 mb-3">
          Members ({members.length})
        </h3>

        {loading ? (
          <div className="text-center py-8 text-neutral-500">Loading...</div>
        ) : members.length === 0 ? (
          <div className="text-center py-8 text-neutral-500">
            <FaUserPlus className="mx-auto text-3xl mb-2 opacity-50" />
            <p>No members yet. {isOwner ? 'Add someone to your trip!' : 'The owner can invite members.'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    member.status === 'active' ? 'bg-green-100' : 'bg-yellow-100'
                  }`}>
                    {member.status === 'active' ? (
                      <FaUserCheck className="text-green-600" />
                    ) : (
                      <FaClock className="text-yellow-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">
                      {member.full_name || member.email}
                      {member.user_id && (
                        <span className="ml-2 text-xs text-neutral-400">#{member.user_id}</span>
                      )}
                    </p>
                    <p className="text-sm text-neutral-500">
                      {member.status === 'active' ? (
                        <span className="text-green-600">Active</span>
                      ) : (
                        <span className="text-yellow-600">Pending invitation</span>
                      )}
                      {member.role === 'owner' && (
                        <span className="ml-2 text-primary-600">(Owner)</span>
                      )}
                    </p>
                  </div>
                </div>

                {isOwner && member.role !== 'owner' && (
                  <button
                    onClick={() => handleRemoveMember(member.id)}
                    className="p-2 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove member"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-neutral-200">
        <p className="text-xs text-neutral-500 text-center">
          Members can view this itinerary and stay updated on trip plans.
        </p>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, memberId: null })}
        onConfirm={confirmRemoveMember}
        title="Remove Member"
        message="Are you sure you want to remove this member from the trip? They will no longer have access to the itinerary."
        confirmText="Remove"
        cancelText="Cancel"
        type="danger"
      />
    </>
  );

  // Inline mode - render directly without modal wrapper
  if (inline) {
    return <div className="p-2">{content}</div>;
  }

  // Modal mode - original behavior
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-elevated max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-primary-500 to-primary-600">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">Group Members</h2>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {content}
        </div>
      </div>
    </div>
  );
};

export default GroupMembers;
